import { extractProperty } from "./extractProperty"

interface Document {
  name: string | undefined
  url: string | undefined
}

interface PhoneNumber {
  name: string | undefined
  url: string | undefined
}

interface EmailAddress {
  name: string | undefined
  url: string | undefined
}

interface OpeningHoursContent {
  day: string | undefined
  hour: string | undefined
}

interface BreakHoursContent {
  hour: string | undefined
}

interface AddressLink {
  name: string | undefined
  target: string | null
  type: string | undefined
  url: string | undefined
}

export interface ContactUsInnerItem {
  itemTitle: string | undefined
  openingHoursTitle: string | undefined
  openingHoursIcon: string | undefined
  openingHoursContent: OpeningHoursContent[]
  breakHoursTitle: string | undefined
  breakHoursIcon: string | undefined
  breakHoursContent: BreakHoursContent[]
  addressTitle: string | undefined
  addressIcon: string | undefined
  address: string | undefined
  addressLink?: AddressLink[]
  faxTitle: string | undefined
  faxIcon: string | undefined
  faxNumber: string | undefined
  importantToKnowTitle: string | undefined
  importantToKnowContent: string | undefined
}

export interface AccordionItem {
  itemTitle: string | undefined
  openingHoursTitle: string | undefined
  openingHoursIcon: string | undefined
  importantToKnowTitle: string | undefined
  importantToKnowContent: string | undefined
  addressTitle: string | undefined
  addressIcon: string | undefined
  address: string | undefined
  addressLink?: AddressLink[]
  breakHoursTitle: string | undefined
  breakHoursIcon: string | undefined
  breakHoursContent: BreakHoursContent[]
  faxTitle: string | undefined
  faxIcon: string | undefined
  faxNumber: string | undefined
  documentsTitle: string | undefined
  documentsIcon: string | undefined
  phoneIcon: string | undefined
  emailTitle: string | undefined
  documents: Document[]
  emailAddress: EmailAddress[]
  openingHoursContent: OpeningHoursContent[]
  phoneNumber: PhoneNumber[]
  itemsList: ContactUsInnerItem[]
  button: {
    name: string | undefined
    url: string | undefined
  }
}

export function getAccordionItems(data: any): AccordionItem[] {
  const accordionBlocks =
    extractProperty(data, "contactInfo") ||
    extractProperty(data, "itemsList") ||
    []

  const accordionItems = accordionBlocks.map((item: any) => {
    const documentsBlock = extractProperty(item, "documents") || []
    const documents: Document[] = documentsBlock.map((document: any) => {
      return {
        name: document.name,
        url: document.url,
      }
    })
    const phoneNumberBlock = extractProperty(item, "phoneNumber") || []
    const phoneNumber: PhoneNumber[] = phoneNumberBlock.map((phone: any) => {
      return {
        name: phone.name,
        url: phone.url,
      }
    })
    const emailAddressBlock = extractProperty(item, "emailAddress") || []
    const emailAddress: EmailAddress[] = emailAddressBlock.map((email: any) => {
      return {
        name: email.name,
        url: email.url,
      }
    })
    const openingHoursContentBlock =
      extractProperty(item, "openingHoursContent") || []
    const openingHoursContent: OpeningHoursContent[] =
      openingHoursContentBlock.map((content: any) => {
        return {
          day: extractProperty(content, "day"),
          hour: extractProperty(content, "hour"),
        }
      })

    const parentBreakHoursBlock =
      extractProperty(item, "breakHoursContent") || []
    const parentBreakHoursContent: BreakHoursContent[] =
      parentBreakHoursBlock.map((content: any) => ({
        hour: extractProperty(content, "hour"),
      }))

    const itemsListBlock = extractProperty(item, "itemsList") || []
    const itemsList: ContactUsInnerItem[] = itemsListBlock.map(
      (innerItem: any) => {
        const innerOpeningHoursBlock =
          extractProperty(innerItem, "openingHoursContent") || []
        const innerOpeningHours: OpeningHoursContent[] =
          innerOpeningHoursBlock.map((content: any) => ({
            day: extractProperty(content, "day"),
            hour: extractProperty(content, "hour"),
          }))

        const breakHoursBlock =
          extractProperty(innerItem, "breakHoursContent") || []
        const breakHoursContent: BreakHoursContent[] = breakHoursBlock.map(
          (content: any) => ({
            hour: extractProperty(content, "hour"),
          }),
        )

        return {
          itemTitle: extractProperty(innerItem, "itemTitle"),
          openingHoursTitle: extractProperty(innerItem, "openingHoursTitle"),
          openingHoursIcon: extractProperty(innerItem, "openingHoursIcon"),
          openingHoursContent: innerOpeningHours,
          breakHoursTitle: extractProperty(innerItem, "breakHoursTitle"),
          breakHoursIcon: extractProperty(innerItem, "breakHoursIcon"),
          breakHoursContent,
          addressTitle: extractProperty(innerItem, "addressTitle"),
          addressIcon: extractProperty(innerItem, "addressIcon"),
          address: extractProperty(innerItem, "address"),
          addressLink: extractProperty(innerItem, "addressLink"),
          faxTitle: extractProperty(innerItem, "faxTitle"),
          faxIcon: extractProperty(innerItem, "faxIcon"),
          faxNumber: extractProperty(innerItem, "faxNumber"),
          importantToKnowTitle: extractProperty(
            innerItem,
            "importantToKnowTitle",
          ),
          importantToKnowContent: extractProperty(
            innerItem,
            "importantToKnowContent",
          ),
        }
      },
    )

    const operatingHours = extractProperty(item, "operatingHours") || []
    const sendTo = extractProperty(item, "sendTo")
    return {
      itemTitle:
        extractProperty(item, "itemTitle") || extractProperty(item, "title"),
      openingHoursTitle: extractProperty(item, "openingHoursTitle"),
      openingHoursIcon: extractProperty(item, "openingHoursIcon"),
      importantToKnowTitle: extractProperty(item, "importantToKnowTitle"),
      importantToKnowContent: extractProperty(item, "importantToKnowContent"),
      addressTitle: extractProperty(item, "addressTitle"),
      addressIcon: extractProperty(item, "addressIcon"),
      address: extractProperty(item, "address"),
      addressLink: extractProperty(item, "addressLink"),
      breakHoursTitle: extractProperty(item, "breakHoursTitle"),
      breakHoursIcon: extractProperty(item, "breakHoursIcon"),
      breakHoursContent: parentBreakHoursContent,
      faxTitle: extractProperty(item, "faxTitle"),
      faxIcon: extractProperty(item, "faxIcon"),
      faxNumber: extractProperty(item, "faxNumber"),
      documentsTitle: extractProperty(item, "documentsTitle"),
      documentsIcon: extractProperty(item, "documentsIcon"),
      phoneIcon: extractProperty(item, "phoneIcon"),
      emailTitle: extractProperty(item, "emailTitle"),
      button: {
        name: sendTo?.[0]?.name,
        url: sendTo?.[0]?.url,
      },
      documents,
      emailAddress,
      openingHoursContent,
      phoneNumber,
      itemsList,
      operatingHours,
    }
  })

  return accordionItems
}
